import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { UserRole } from '@spi/shared';

export function HomePage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isAdmin = user?.role === UserRole.ADMIN;

  return (
    <div className="relative">
      {/* Hero Section */}
      <section className="relative overflow-hidden py-16 md:py-24 lg:py-32">
        {/* Background image with overlay */}
        <div className="absolute inset-0">
          <img src="/images/hero-bg.jpg" alt="" className="w-full h-full object-cover" loading="eager" fetchPriority="high" />
          <div className="absolute inset-0 bg-gradient-to-b from-white/85 via-spi-cream/80 to-white" />
        </div>

        {/* Background decorations */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-20 left-10 w-72 h-72 blob-green rounded-full" />
          <div className="absolute bottom-20 right-10 w-96 h-96 blob-orange rounded-full" />
        </div>

        <div className="container mx-auto px-4 relative">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-spi-seed border border-spi-parchment mb-6 animate-fade-in-up">
              <span className="seed-dot" />
              <span className="text-sm font-medium text-spi-earth">
                {t('home.badge')}
              </span>
            </div>

            <h1 className="mb-6 animate-fade-in-up animation-delay-100">
              {t('home.heroTitle1')} <span className="text-gradient-green">{t('home.heroTitle2')}</span>
              <br />
              {t('home.heroTitle3')}
            </h1>

            <p className="text-xl text-spi-slate max-w-2xl mx-auto mb-10 animate-fade-in-up animation-delay-200">
              {t('home.heroDescription')}
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-fade-in-up animation-delay-300">
              <Link to="/seed-calculator" className="btn btn-primary text-lg">
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                  />
                </svg>
                {t('home.seedCalculatorBtn')}
              </Link>
              <Link to="/marketplace" className="btn bg-spi-orange text-white hover:bg-spi-orange-dark text-lg">
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
                  />
                </svg>
                {t('home.marketplaceBtn')}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Tools Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="section-header">
            <h2>{t('home.toolsSectionTitle')}</h2>
            <p>
              {t('home.toolsSectionDescription')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {/* Seed Calculator Card - Active */}
            <Link
              to="/seed-calculator"
              className="card card-featured text-left group cursor-pointer"
            >
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-spi-green to-spi-green-dark flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
                <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <h3 className="text-xl mb-2 group-hover:text-spi-green transition-colors">
                {t('home.seedCalcCardTitle')}
              </h3>
              <p className="text-spi-slate text-sm mb-4">
                {t('home.seedCalcCardDescription')}
              </p>
              <span className="inline-flex items-center gap-2 text-spi-green font-semibold text-sm">
                {t('home.openCalculator')}
                <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </span>
            </Link>

            {/* Seed Marketplace Card - Active */}
            <Link
              to="/marketplace"
              className="card card-featured text-left group cursor-pointer relative overflow-hidden"
            >
              <div className="absolute top-4 right-4 px-2 py-1 rounded-full bg-spi-orange/10 text-spi-orange text-xs font-semibold">
                {t('home.beta')}
              </div>
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-spi-orange to-spi-orange-dark flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
                <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
                  />
                </svg>
              </div>
              <h3 className="text-xl mb-2 group-hover:text-spi-orange transition-colors">
                {t('home.marketplaceTitle')}
              </h3>
              <p className="text-spi-slate text-sm mb-4">
                {t('home.marketplaceDescription')}
              </p>
              <span className="inline-flex items-center gap-2 text-spi-orange font-semibold text-sm">
                Visit Network
                <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </span>
            </Link>

            {/* Custom Solutions Card */}
            <Link
              to="/contact"
              className="card card-featured text-left group cursor-pointer"
            >
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-teal-500 to-blue-600 flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
                <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <h3 className="text-xl mb-2 group-hover:text-teal-600 transition-colors">
                {t('home.customSolutionsTitle')}
              </h3>
              <p className="text-spi-slate text-sm mb-4">
                {t('home.customSolutionsDescription')}
              </p>
              <span className="inline-flex items-center gap-2 text-teal-600 font-semibold text-sm">
                {t('home.getInTouch')}
                <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </span>
            </Link>

            {/* Variety Mapper Card - Admin only, Alpha */}
            {isAdmin && (
              <Link
                to="/variety-mapper"
                className="card card-featured text-left group cursor-pointer relative overflow-hidden"
              >
                <div className="absolute top-4 right-4 px-2 py-1 rounded-full bg-indigo-100 text-indigo-600 text-xs font-semibold">
                  Alpha
                </div>
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-500 to-indigo-700 flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
                  <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                </div>
                <h3 className="text-xl mb-2 group-hover:text-indigo-600 transition-colors">
                  {t('home.varietyMapperTitle')}
                </h3>
                <p className="text-spi-slate text-sm mb-4">
                  {t('home.varietyMapperDescription')}
                </p>
                <span className="inline-flex items-center gap-2 text-indigo-600 font-semibold text-sm">
                  {t('home.openMapper')}
                  <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </span>
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-16 md:py-24 bg-pattern-seeds">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="card card-dark p-8 md:p-12">
              <div className="flex flex-col md:flex-row items-start gap-8">
                <div className="flex-shrink-0">
                  <div className="w-20 h-20 rounded-2xl bg-white/10 flex items-center justify-center">
                    <svg viewBox="0 0 24 24" className="w-10 h-10 text-spi-orange" fill="currentColor">
                      <path d="M12 2C9 2 6 5 6 10c0 4 3 8 6 12 3-4 6-8 6-12 0-5-3-8-6-8zm0 11c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3z" />
                    </svg>
                  </div>
                </div>
                <div>
                  <h2 className="text-2xl md:text-3xl mb-4">{t('home.aboutTitle')}</h2>
                  <p className="text-white/80 mb-6 leading-relaxed">
                    {t('home.aboutDescription')}
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Link
                      to="/seed-calculator"
                      className="btn bg-white text-spi-green-dark hover:bg-spi-cream"
                    >
                      {t('home.seedCalculatorBtn')}
                    </Link>
                    <Link
                      to="/marketplace"
                      className="btn bg-spi-orange text-white hover:bg-spi-orange-dark"
                    >
                      {t('home.marketplaceBtn')}
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 md:py-20 bg-gradient-to-b from-white to-spi-cream">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
            <div className="stat-card">
              <div className="stat-value text-spi-green">{t('home.featureFast')}</div>
              <div className="stat-label">{t('home.featureFastLabel')}</div>
            </div>
            <div className="stat-card">
              <div className="stat-value text-spi-green">{t('home.featureFree')}</div>
              <div className="stat-label">{t('home.featureFreeLabel')}</div>
            </div>
            <div className="stat-card">
              <div className="stat-value text-spi-green">{t('home.featureEasy')}</div>
              <div className="stat-label">{t('home.featureEasyLabel')}</div>
            </div>
            <div className="stat-card">
              <div className="stat-value text-spi-green">{t('home.featureAccurate')}</div>
              <div className="stat-label">{t('home.featureAccurateLabel')}</div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
